/**
 * Scoped error boundary around message content. A render-time crash in the
 * markdown renderer (or any child) must never silently blank the conversation
 * — it shows an inline fallback instead of unmounting the whole panel.
 */

import React from 'react';
import { Alert, Typography } from '@mui/material';

interface Props {
  children: React.ReactNode;
}

interface State {
  error: Error | null;
}

class ChatErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ai-chat] message render error', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <Alert severity="warning" sx={{ my: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            This message could not be displayed.
          </Typography>
          {import.meta.env.DEV && (
            <Typography
              variant="caption"
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', mt: 0.5, opacity: 0.8 }}
            >
              {this.state.error.message}
            </Typography>
          )}
        </Alert>
      );
    }
    return this.props.children;
  }
}

export default ChatErrorBoundary;
